const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.toMillis === 'function') return new Date(value.toMillis());
  if (value.seconds) return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getDayKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfDay = (date) => new Date(
  date.getFullYear(),
  date.getMonth(),
  date.getDate()
);

const getRangeStart = (rangeDays, now) => {
  if (!rangeDays) return null;
  const start = startOfDay(now);
  start.setDate(start.getDate() - (rangeDays - 1));
  return start;
};

const buildDailyTrend = (views, rangeDays, now) => {
  const trendDays = rangeDays ? Math.min(rangeDays, 30) : 30;
  const counts = views.reduce((result, view) => ({
    ...result,
    [view.day]: (result[view.day] || 0) + 1
  }), {});

  return Array.from({ length: trendDays }, (unused, index) => {
    const date = startOfDay(now);
    date.setDate(date.getDate() - (trendDays - index - 1));
    const day = getDayKey(date);
    return { day, views: counts[day] || 0 };
  });
};

export const buildProfileAnalytics = ({
  aliasesByUser = {},
  now = new Date(),
  rangeDays = 30,
  users = [],
  views = []
}) => {
  const normalizedViews = views.map((view) => {
    const viewedAt = toDate(view.viewedAt);
    return {
      ...view,
      day: view.day || (viewedAt ? getDayKey(viewedAt) : ''),
      viewedAt,
      viewedAtTime: viewedAt ? viewedAt.getTime() : 0
    };
  });
  const rangeStart = getRangeStart(rangeDays, now);
  const filteredViews = normalizedViews.filter((view) => (
    !rangeStart || view.viewedAtTime >= rangeStart.getTime()
  ));
  const usersById = users.reduce((result, user) => ({
    ...result,
    [user.id]: user
  }), {});
  const groupedUsers = {};

  filteredViews.forEach((view) => {
    const userId = view.userId || 'unknown';
    const current = groupedUsers[userId] || {
      userId,
      views: 0,
      visitorIds: new Set(),
      profileIds: new Set(),
      lastViewTime: 0
    };
    current.views += 1;
    current.visitorIds.add(view.visitorId || view.sessionId || view.id);
    if (view.profileId) current.profileIds.add(view.profileId);
    current.lastViewTime = Math.max(current.lastViewTime, view.viewedAtTime);
    groupedUsers[userId] = current;
  });

  const topUsers = Object.values(groupedUsers)
    .map((entry) => {
      const user = usersById[entry.userId] || {};
      return {
        userId: entry.userId,
        name: user.fullname || 'Unknown user',
        email: user.email || '',
        aliases: Array.from(new Set([
          ...(aliasesByUser[entry.userId] || []),
          ...entry.profileIds
        ])),
        views: entry.views,
        uniqueVisitors: entry.visitorIds.size,
        lastViewTime: entry.lastViewTime,
        share: filteredViews.length
          ? Math.round((entry.views / filteredViews.length) * 1000) / 10
          : 0
      };
    })
    .sort((first, second) => (
      second.views - first.views || second.lastViewTime - first.lastViewTime
    ));

  const sourceCounts = filteredViews.reduce((result, view) => {
    const source = view.referrerHost || 'Direct';
    return { ...result, [source]: (result[source] || 0) + 1 };
  }, {});
  const deviceCounts = filteredViews.reduce((result, view) => {
    const device = view.deviceType || 'Unknown';
    return { ...result, [device]: (result[device] || 0) + 1 };
  }, {});
  const toBreakdown = (counts) => Object.entries(counts)
    .map(([label, count]) => ({
      label,
      count,
      share: filteredViews.length
        ? Math.round((count / filteredViews.length) * 1000) / 10
        : 0
    }))
    .sort((first, second) => second.count - first.count);
  const todayKey = getDayKey(now);

  return {
    allTimeViews: normalizedViews.length,
    rangeViews: filteredViews.length,
    todayViews: normalizedViews.filter((view) => view.day === todayKey).length,
    uniqueVisitors: new Set(filteredViews.map(
      (view) => view.visitorId || view.sessionId || view.id
    )).size,
    viewedProfiles: new Set(filteredViews.map((view) => view.userId).filter(Boolean)).size,
    topUsers,
    sources: toBreakdown(sourceCounts),
    devices: toBreakdown(deviceCounts),
    dailyTrend: buildDailyTrend(normalizedViews, rangeDays, now)
  };
};

export const formatAnalyticsDate = (timestamp) => (
  timestamp
    ? new Date(timestamp).toLocaleString()
    : 'No recorded view'
);

